import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useState } from "react";
import type { TestShackReportSectionRev } from "../models/TestShackReportSectionRev";
import { useTestShackReportDrawingPreviewQuery } from "@/pages/products/test-shack/hooks/queries/useTestShackReportDrawingPreviewQuery";
import { TestShackReportDrawingFrame } from "@/pages/products/test-shack/shared/components/report/TestShackReportDrawingFrame";
import { IMAGE_KIND, type ImageKind } from "./TestShackReportImageDialog";

interface TestShackReportDrawingPanelProps {
	sectionRev: TestShackReportSectionRev;
	onError: (error: Error) => void;
	onOpen: (kind: ImageKind) => void;
}

interface DrawingView {
	kind: ImageKind;
	label: string;
	preview: ReturnType<typeof useTestShackReportDrawingPreviewQuery>;
}

export const TestShackReportDrawingPanel = ({
	sectionRev,
	onError,
	onOpen,
}: TestShackReportDrawingPanelProps) => {
	const [preferredKind, setPreferredKind] = useState<ImageKind>(
		IMAGE_KIND.DIMENSIONS,
	);

	const millPreview = useTestShackReportDrawingPreviewQuery({
		uploadFileId: sectionRev.millDrawingUploadFileId,
		onError,
	});
	const dimensionsPreview = useTestShackReportDrawingPreviewQuery({
		uploadFileId: sectionRev.dimensionsReferenceUploadFileId,
		onError,
	});
	const views: DrawingView[] = [
		...(sectionRev.millDrawingUploadFileId !== null
			? [
					{
						kind: IMAGE_KIND.MILL_DRAWING,
						label: "Mill drawing",
						preview: millPreview,
					},
				]
			: []),
		...(sectionRev.dimensionsReferenceUploadFileId !== null
			? [
					{
						kind: IMAGE_KIND.DIMENSIONS,
						label: "Dimensions reference",
						preview: dimensionsPreview,
					},
				]
			: []),
	];
	const activeView =
		views.find((view) => view.kind === preferredKind) ?? views[0] ?? null;

	return (
		<TestShackReportDrawingFrame
			sectionRev={sectionRev}
			label={activeView?.label ?? ""}
			preview={activeView?.preview ?? null}
			onOpen={() => activeView && onOpen(activeView.kind)}
		>
			{views.length > 1 && (
				<ToggleButtonGroup
					exclusive
					fullWidth
					size="small"
					value={activeView?.kind ?? null}
					onChange={(_event, value: ImageKind | null) => {
						if (value !== null) setPreferredKind(value);
					}}
					aria-label="Drawing type"
					sx={{ mb: 0.75 }}
				>
					{views.map((view) => (
						<ToggleButton
							key={view.kind}
							value={view.kind}
							sx={{ fontSize: "10px", py: 0.25, textTransform: "none" }}
						>
							{view.label}
						</ToggleButton>
					))}
				</ToggleButtonGroup>
			)}
		</TestShackReportDrawingFrame>
	);
};
